/**
 * @fileoverview ThemeProvider - Theme management component with 8 standalone themes.
 *
 * Manages application theme state with localStorage persistence. Supports 8 standalone
 * themes (4 light + 4 dark) instead of the old two-layer system.
 *
 * @description This component provides:
 * - 8 standalone theme options (Ocean Blue, Forest Green, Royal Purple, Sunset Orange,
 *   Ocean Blue Dark, Midnight Dark, Warm Dark, Graphite Dark)
 * - localStorage persistence
 * - Theme context for child components
 * - Migration logic for users from old two-layer system
 *
 * Usage:
 * Wrap your app with <ThemeProvider> and use useTheme() hook in components
 *
 * @module components/ThemeProvider
 */

import React, { createContext, useState, useEffect, useContext } from 'react';

/**
 * Available color themes (8 standalone themes)
 */
export const COLOR_THEMES = {
  // Light themes
  BLUE: 'blue',
  GREEN: 'green',
  PURPLE: 'purple',
  ORANGE: 'orange',

  // Dark themes
  BLUE_DARK: 'blue-dark',
  MIDNIGHT_DARK: 'midnight-dark',
  WARM_DARK: 'warm-dark',
  GRAPHITE_DARK: 'graphite-dark'
};

/**
 * Theme display labels
 */
export const THEME_LABELS = {
  blue: 'Ocean Blue',
  green: 'Forest Green',
  purple: 'Royal Purple',
  orange: 'Sunset Orange',
  'blue-dark': 'Ocean Blue Dark',
  'midnight-dark': 'Midnight Dark',
  'warm-dark': 'Warm Dark',
  'graphite-dark': 'Graphite Dark'
};

/**
 * Theme context for accessing theme state and functions.
 *
 * @type {React.Context}
 */
const ThemeContext = createContext({
  colorTheme: COLOR_THEMES.BLUE,
  setColorTheme: () => {}
});

/**
 * Hook to access theme context in components.
 *
 * @returns {Object} Theme context
 * @returns {string} return.colorTheme - Current theme (one of 8 options)
 * @returns {Function} return.setColorTheme - Function to change theme
 *
 * @example
 * const { colorTheme, setColorTheme } = useTheme();
 * console.log(colorTheme); // 'blue', 'blue-dark', 'midnight-dark', etc.
 * setColorTheme('midnight-dark'); // Changes to Midnight Dark theme
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

/**
 * ThemeProvider component that manages application theme.
 *
 * Loads saved preference from localStorage (with migration from old two-layer system)
 * and provides theme context to all child components.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement} ThemeProvider wrapper
 *
 * @example
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 */
export const ThemeProvider = ({ children }) => {
  // Get initial theme with migration logic
  const getInitialTheme = () => {
    const savedColorTheme = localStorage.getItem('colorTheme');
    const savedTheme = localStorage.getItem('theme'); // Old 'light' or 'dark'

    // Migrate users from old two-layer system to new standalone themes
    if (savedTheme === 'dark' && savedColorTheme) {
      const darkThemeMap = {
        'blue': 'blue-dark',       // Perfect match
        'green': 'graphite-dark',  // Green dark wasn't good, use Graphite
        'purple': 'warm-dark',     // Purple dark wasn't good, use Warm
        'orange': 'midnight-dark'  // Orange dark wasn't good, use Midnight
      };
      const migrated = darkThemeMap[savedColorTheme] || 'blue-dark';

      // Clean up old localStorage key and save migrated theme
      localStorage.removeItem('theme');
      localStorage.setItem('colorTheme', migrated);

      return migrated;
    }

    // Light mode users keep their color theme
    if (savedColorTheme && Object.values(COLOR_THEMES).includes(savedColorTheme)) {
      // Clean up old theme key if it exists
      if (savedTheme) {
        localStorage.removeItem('theme');
      }
      return savedColorTheme;
    }

    // Default for new users
    return COLOR_THEMES.BLUE;
  };

  const [colorTheme, setColorThemeState] = useState(getInitialTheme);

  // Apply color theme to HTML element
  useEffect(() => {
    document.documentElement.setAttribute('data-color-theme', colorTheme);
    localStorage.setItem('colorTheme', colorTheme);

    // Remove old data-theme attribute if it exists
    document.documentElement.removeAttribute('data-theme');
  }, [colorTheme]);

  /**
   * Changes the color theme.
   *
   * @param {string} newColorTheme - The new theme (one of 8 options)
   */
  const setColorTheme = (newColorTheme) => {
    if (Object.values(COLOR_THEMES).includes(newColorTheme)) {
      setColorThemeState(newColorTheme);
    }
  };

  const value = {
    colorTheme,
    setColorTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
